// useJanetOffers — the signed-in person's open offers from Janet's offers pass
// (janet_offers, own rows only under RLS), plus the two rulings a person can
// make on one: accept (Janet starts on it) and dismiss. Both writes check the
// returned row, so a click that changed nothing never reads as success.
//
// Degrades quietly before migration 20261012_janet_offers.sql: `available`
// is false and Home shows nothing.

import { useCallback, useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import { MAX_OFFERS, buildOfferRulingPatch } from '../lib/janetOffers'
import { visibleUnderFamilyGate, filterByFamilyGate } from '../lib/janetSourceScope'
import { familyEnabledInBrowser } from '../lib/janetFamilyGateClient'

const BASE_COLS = 'id, rank, title, why, people, deadline_at, deadline_label, action_type, action_label, prompt, sources, status, run_at, draft_state, draft_owner, draft_next_action, draft_waiting_on, draft_queue_id'
// origin_message_id doesn't exist until supabase/migrations/20261018_janet_reply_alerts.sql
// lands. Needed here because offerOriginKey (src/lib/janetOpenerKeys.js)
// reads it to compute the SAME stable per-message identity key the worker's
// own opener (api/_lib/janet-openers.js, via api/_lib/janet-offers.js's
// loadOpenOffers) uses — without it, an offer read through this module
// always fell back to the title-based key even once the column existed, so
// the browser's own fallback topic creation (src/pages/JanetChatPage.jsx's
// ensureOriginSession) and the worker's opener would key the SAME offer two
// different ways and never converge on one topic (review round 4, finding #5).
const COLS = `${BASE_COLS}, origin_message_id`
let originColMissing = false
function isMissingOriginCol(error) {
  return !!error && /origin_message_id/i.test(error?.message || '')
}

export async function fetchOffer(id) {
  const cols = originColMissing ? BASE_COLS : COLS
  let { data, error } = await supabase.from('janet_offers').select(cols).eq('id', id).maybeSingle()
  if (error && !originColMissing && isMissingOriginCol(error)) {
    originColMissing = true
    ;({ data, error } = await supabase.from('janet_offers').select(BASE_COLS).eq('id', id).maybeSingle())
  }
  if (error) return { offer: null, error: error.message }
  const offer = data && visibleUnderFamilyGate(data, familyEnabledInBrowser()) ? data : null
  return { offer, error: offer ? null : 'This offer could not be found.' }
}

export async function ruleOnOffer(id, status, via = 'home') {
  let offer = null
  if (status === 'accepted') {
    const result = await fetchOffer(id)
    if (!result.offer) return { ok: false, error: result.error }
    offer = result.offer
  }
  // buildOfferRulingPatch (src/lib/janetOffers.js) is the one place the
  // draft_ prefix decision is made — shared with anything that needs the
  // real acceptance contract without importing this module (review round 3,
  // finding #5).
  const cols = originColMissing ? BASE_COLS : COLS
  const patch = buildOfferRulingPatch(offer, status, via)   // computed once — a retry on missing-column must not re-stamp a different acted_at
  let { data, error } = await supabase.from('janet_offers')
    .update(patch)
    .eq('id', id)
    .select(cols)
  if (error && !originColMissing && isMissingOriginCol(error)) {
    originColMissing = true
    ;({ data, error } = await supabase.from('janet_offers')
      .update(patch)
      .eq('id', id)
      .select(BASE_COLS))
  }
  if (error) return { ok: false, error: error.message }
  if (!data?.length) return { ok: false, error: 'Nothing was changed — the offer may belong to someone else.' }
  return { ok: true, offer: data[0] }
}

// Review round 5, finding #1: the status flip to 'accepted' and the owner's
// actual instruction must land in ONE write, not a status update followed by
// a separate janet_chat_messages insert — a crash or failed write between
// the two used to leave claimable work (status='accepted') with no durable
// instruction, and the worker fell back to the offer's own canned `prompt`.
// accept_janet_offer_with_instruction (supabase/migrations/20261018_janet_reply_alerts.sql)
// does both the status flip AND the accepted_instruction write in the SAME
// UPDATE statement — either the whole thing commits or none of it does.
//
// Degrades quietly before that migration lands: a missing-function error
// (PGRST202, or a Postgres "does not exist" on a fresh schema cache) is
// reported as `unavailable`, never thrown — the caller falls back to the
// older two-step accept (ruleOnOffer + a separate chat-message save) exactly
// as it worked before this function existed, same convention as
// originColMissing above.
let acceptInstructionRpcMissing = false
function isMissingAcceptFunction(error) {
  const msg = String(error?.message || '')
  return error?.code === 'PGRST202' || /function .*accept_janet_offer_with_instruction.* does not exist|could not find function/i.test(msg)
}

export async function acceptOfferWithInstruction(id, instruction) {
  const text = String(instruction ?? '').trim()
  if (!text) return { ok: false, error: 'An instruction is required' }
  if (acceptInstructionRpcMissing) return { ok: false, unavailable: true, error: 'Not available yet' }
  const { data, error } = await supabase.rpc('accept_janet_offer_with_instruction', { p_id: id, p_instruction: text })
  if (error) {
    if (isMissingAcceptFunction(error)) { acceptInstructionRpcMissing = true; return { ok: false, unavailable: true, error: error.message } }
    return { ok: false, error: error.message }
  }
  // RETURNS SETOF — real PostgREST returns an array (same shape as
  // claim_janet_offer_draft, which the worker already reads as data?.[0]).
  // Zero rows means the WHERE clause's own guards rejected it (wrong owner,
  // no longer 'open', or an empty instruction already caught above) — never
  // an exception, the same idiom claim_janet_offer_draft already uses.
  const offer = Array.isArray(data) ? data[0] : data
  if (!offer) return { ok: false, error: 'Nothing was changed — the offer may belong to someone else, or is no longer open.' }
  return { ok: true, offer }
}

export function useJanetOffers(userId) {
  const [offers, setOffers] = useState([])
  const [loading, setLoading] = useState(true)
  const [available, setAvailable] = useState(true)

  const load = useCallback(async () => {
    if (!userId) { setOffers([]); setLoading(false); return }
    const cols = originColMissing ? BASE_COLS : COLS
    let { data, error } = await supabase.from('janet_offers')
      .select(cols)
      .eq('user_id', userId)
      .eq('status', 'open')
      .order('run_at', { ascending: false })
      .order('rank', { ascending: true })
      .limit(MAX_OFFERS)
    if (error && !originColMissing && isMissingOriginCol(error)) {
      originColMissing = true
      ;({ data, error } = await supabase.from('janet_offers')
        .select(BASE_COLS)
        .eq('user_id', userId)
        .eq('status', 'open')
        .order('run_at', { ascending: false })
        .order('rank', { ascending: true })
        .limit(MAX_OFFERS))
    }
    if (error) {
      setAvailable(false)
      setOffers([])
      console.info('[janet-offers] not available yet:', error.message)
    } else {
      setAvailable(true)
      setOffers(filterByFamilyGate(data, familyEnabledInBrowser()))
    }
    setLoading(false)
  }, [userId])

  useEffect(() => { load() }, [load])

  const dismiss = useCallback(async (id) => {
    const res = await ruleOnOffer(id, 'dismissed', 'home')
    if (res.ok) setOffers(prev => prev.filter(o => o.id !== id))
    return res
  }, [])

  return { offers, loading, available, reload: load, dismiss }
}
